import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import { isEventEnded } from '@aurora/shared-client/helpers/forms/DateHelper/DateHelper';
import type { OccasionTopicMessage } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { OccasionCoverImageFragment } from '../../../types/graphql-types';
import MessageCoverImage from '../../messages/MessageCoverImage/MessageCoverImage';
import MessageSubject from '../../messages/MessageSubject/MessageSubject';
import useAutoRefresh from '../../useAutoRefresh';
import useTranslation from '../../useTranslation';
import OccasionProgressBadge from '../OccasionProgressBadge/OccasionProgressBadge';
import OccasionRsvpInformation from '../OccasionRsvpInformation/OccasionRsvpInformation';
import localStyles from './OccasionMessageCoverWidget.module.pcss';
import OccasionAttendees from '../OccasionAttendees/OccasionAttendees';

interface Props {
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
}

/**
 * Widget for Occasion Cover
 *
 * @author Aditi Agarwal, Willi Hyde
 */
const OccasionMessageCoverWidget: React.FC<React.PropsWithChildren<Props>> = ({ className }) => {
  const cx = useClassNameMapper(localStyles);
  const { contextMessage } = useContext(AppContext);
  const { formatMessage } = useTranslation(EndUserComponent.OCCASION_MESSAGE_COVER_WIDGET);
  const occasion = contextMessage as OccasionTopicMessage;
  const { occasionType, startTime, endTime, liveVideoIframe } = occasion.occasionData || {};
  const eventHappeningNow = useAutoRefresh(startTime, endTime, liveVideoIframe, 3600000);
  const eventEnded = isEventEnded(new Date(endTime));
  const hasImage = (contextMessage as OccasionCoverImageFragment)?.coverImage?.url ? true : false;
  const hasVideo = liveVideoIframe ? true : false;
  const hasActiveVideo = hasVideo && eventHappeningNow;
  const { isAnonymous } = useRegistrationStatus();

  // Gets the position of the attendees based on the context
  let attendeesPosition: 'left' | 'right' | 'none' = 'right';
  if (isAnonymous) {
    attendeesPosition = 'none';
  } else if (hasVideo && (!hasImage || eventHappeningNow)) {
    attendeesPosition = 'left';
  }

  // Gets the position of the RSVP based on the context
  let rsvpPosition: 'overlayed' | 'static' = 'static';
  if (hasImage && (!hasVideo || (hasVideo && !eventHappeningNow))) {
    rsvpPosition = 'overlayed';
  }

  return (
    <div className={cx('lia-container', className)} data-testid="OccasionMessageCoverWidget">
      <MessageCoverImage
        message={contextMessage as OccasionTopicMessage}
        isCreateOrEditView={false}
      />
      <div
        className={cx('lia-wrapper lia-g-message-box', {
          'lia-no-banner': !hasImage,
          'lia-no-active-video': !hasActiveVideo
        })}
      >
        <div className={cx('lia-info')}>
          <MessageSubject
            message={contextMessage}
            useLink={false}
            as="h1"
            className={cx('lia-subject')}
          />
          <div className={cx('lia-meta')}>
            <OccasionProgressBadge
              occasionData={(contextMessage as OccasionTopicMessage).occasionData}
              showFullTimeText
            />
            <small className={cx('flex-shrink-0')}>{formatMessage(occasionType)}</small>
            {attendeesPosition === 'left' && (
              <OccasionAttendees className={cx('lia-attendees')} occasion={occasion} />
            )}
          </div>
        </div>
        {!eventEnded && (
          <div className={cx('lia-rsvp')}>
            <OccasionRsvpInformation
              occasion={occasion}
              className={cx({ 'lia-overlay-mode': rsvpPosition === 'overlayed' })}
            />
            {attendeesPosition === 'right' && (
              <OccasionAttendees className={cx('lia-attendees')} occasion={occasion} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OccasionMessageCoverWidget;
